import React, { useEffect, useState } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import showValidateErrors from "../../common/utils/showValidateErrors";
import { useLanguage } from "../../common/i18n/LanguageContext";

function AddResultModal({ isOpen, onClose, onSuccess, lottotype }) {
  const { t } = useLanguage();
  const [lottoTypes, setLottoTypes] = useState([]);
  const [lottoType, setLottoType] = useState(lottotype ?? "th_gov");
  const [resultDate, setResultDate] = useState(new Date());
  const [round, setRound] = useState(1);
  const [drawResultTop, setDrawResultTop] = useState("");
  const [drawResultButtom, setDrawResultButtom] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) setLottoType(lottotype ?? "th_gov");
  }, [isOpen, lottotype]);

  const roundOptions =
    lottoTypes.find((lt) => lt.type === lottoType)?.rounds || [];

  useEffect(() => {
    const firstRound = roundOptions[0]?.round;
    if (firstRound != null) setRound(firstRound);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottoType, lottoTypes]);

  if (!isOpen) return null;

  const resetForm = () => {
    setLottoType(lottotype ?? "th_gov");
    setResultDate(new Date());
    setDrawResultTop("");
    setDrawResultButtom("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Pre-fills the numbers from the external source (GET
  // /result/sources/fetch) — nothing is saved until the admin presses Save.
  const handleFetchFromSource = () => {
    const params = new URLSearchParams({
      lottotype: lottoType,
      date: moment(resultDate).format("YYYY-MM-DD"),
      round: Number(round),
    });
    setIsFetching(true);
    ApiConfig.get(`/result/sources/fetch?${params.toString()}`)
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        setDrawResultTop(result.data.drawResultTop ?? "");
        setDrawResultButtom(result.data.drawResultButtom ?? "");
        toast.success(t("add_result_fetch_success"), { autoClose: 2000 });
      })
      .catch(() => {
        toast.error(t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .finally(() => setIsFetching(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!drawResultTop.trim()) {
      toast.error(t("add_result_top_required"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (lottoType !== "laos_gov" && !drawResultButtom.trim()) {
      toast.error(t("add_result_bottom_required"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const json = {
      date: moment(resultDate).format("YYYY-MM-DD"),
      lottoType,
      round: Number(round),
      drawResultTop: drawResultTop.trim(),
      ...(drawResultButtom.trim()
        ? { drawResultButtom: drawResultButtom.trim() }
        : {}),
    };

    ApiConfig.post("/addresult", json).then((result) => {
      if (result.resCode !== 200) {
        if (Array.isArray(result.errors) && result.errors.length > 0) {
          showValidateErrors(result.errors);
        } else {
          toast.error(result.message || t("add_result_save_error"), {
            position: "top-right",
            autoClose: 3000,
          });
        }
        return;
      }
      toast.success(t("add_result_save_success"), { autoClose: 2000 });
      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-[420px] max-w-[90%] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-brand-blue-700">
            {t("add_result_title")}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("add_result_lotto_type_label")}
            </label>
            <select
              className="border border-gray-300 rounded px-2 py-1"
              value={lottoType}
              onChange={(e) => setLottoType(e.target.value)}
            >
              {lottoTypes.map((lt) => (
                <option key={lt.id} value={lt.type}>
                  {lt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("add_result_date_label")}
            </label>
            <DatePicker
              className="border border-gray-300 rounded px-2 py-1 w-full"
              selected={resultDate}
              onChange={(date) => setResultDate(date)}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("add_result_round_label")}
            </label>
            <select
              className="border border-gray-300 rounded px-2 py-1"
              value={round}
              onChange={(e) => setRound(e.target.value)}
            >
              {roundOptions.map((r) => (
                <option key={r.id} value={r.round}>
                  {r.round}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleFetchFromSource}
            disabled={isFetching}
            className="h-[2.2rem] bg-brand-blue-600 hover:brightness-110 text-white text-[14px] font-bold rounded disabled:opacity-60"
          >
            {isFetching
              ? t("add_result_fetching")
              : t("add_result_fetch_button")}
          </button>

          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("add_result_top_label")} ({lottoType === "th_gov" ? "6" : "5"}{" "}
              {t("add_result_digits_suffix")})
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-2 py-1"
              value={drawResultTop}
              onChange={(e) => setDrawResultTop(e.target.value)}
              placeholder={
                lottoType === "th_gov"
                  ? t("add_result_top_placeholder_th_gov")
                  : t("add_result_top_placeholder_other")
              }
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold mb-1">
              {t("add_result_bottom_label")} (2 {t("add_result_digits_suffix")})
            </label>
            <input
              type="text"
              className="border border-gray-300 rounded px-2 py-1"
              value={drawResultButtom}
              onChange={(e) => setDrawResultButtom(e.target.value)}
              placeholder={t("add_result_bottom_placeholder")}
            />
          </div>

          <div className="flex flex-row gap-2 mt-2">
            <button
              type="submit"
              className="flex-1 h-[2.2rem] bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 text-[14px] font-bold rounded"
            >
              {t("add_result_save_button")}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-[2.2rem] bg-slate-400 hover:bg-slate-500 text-white text-[14px] font-bold rounded"
            >
              {t("common_cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddResultModal;
